import { useState, useMemo, useCallback, MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Card,
  CardMedia,
  Stack,
  Tooltip,
  IconButton,
  Modal,
  Box,
} from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';

import { Building, Unit, LeaseLifecycle, Process, ActionItem } from '@urbanmix-tech/shared-js';

import { useUIContext } from '@/shared/context/UIContextProvider';

import { useAuth } from '@/shared/context/AuthProvider';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';

import { deleteProject } from '@/data';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { DATE_PERIOD_COLORS, getDatePeriod as getDatePeriodHelper } from '@/constants/datePeriods';
import BuildingDisplayModelGeneric from '@/components/BuildingModel/BuildingDisplayModelGeneric';
import { NewProjectModalContent } from '@/components/NewProjectModalContent/NewProjectModalContent';

import { ErrorBoundary } from '@sentry/react';
import { StatusBar } from './StatusBar';
import { ProcessStatusBar } from './ProcessStatusBar';
import { DateStatusBar } from './DateStatusBar';
import { GenericDistinctVisualizer, colorScales } from '@/components/BuildingTabs/BuildingVisualizers';

export type ProjectCardProps = {
  hasActiveAction?: boolean;
  projectId: string;
  buildingId: number;
  building: Building;
  units: Unit[];
  processes?: Process[];
  actionItems?: ActionItem[];
  name: string;
  address: string;
  stats: Record<
    LeaseLifecycle,
    {
      units: number;
      percentage: number;
    }
  >;
  width: number;
  height?: number; // Optional height
  statusFilter: string[];
  processStatusFilter?: string[];
  processTypeFilter?: string[];
  unitProcessFilter?: string[];
  datePeriodFilter?: string[];
  viewType?: 'lease' | 'process' | 'date';
};

export function ProjectCard({
  hasActiveAction,
  projectId,
  buildingId,
  building,
  units,
  processes,
  actionItems,
  name,
  address,
  stats,
  width,
  height,
  statusFilter,
  processStatusFilter = [],
  processTypeFilter = [],
  unitProcessFilter = [],
  datePeriodFilter = [],
  viewType = 'lease',
}: ProjectCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { openConfirm } = useUIContext();
  const { userData, isEditMode } = useAuth();
  const { getProcessTypeConfig } = useProcessSettings();

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleEditProject: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleDeleteProject: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    openConfirm({
      title: 'Archive Project',
      description: 'Are you sure?',
      onAction: async (value: boolean) => {
        if (value) {
          await deleteProject(projectId, true);
          window.location.reload();
        }
      },
    });
  };

  // Calculate process indicators
  const filteredProcesses = processes?.filter((p) => {
    if (processStatusFilter.length > 0 && !processStatusFilter.includes(p.status)) {
      return false;
    }
    if (processTypeFilter.length > 0 && p.type && !processTypeFilter.includes(p.type)) {
      return false;
    }
    return true;
  }) || [];

  const activeProcessCount = filteredProcesses.filter((p) => p.status === 'start').length;
  const activeUnitIds = new Set(
    filteredProcesses.filter((p) => p.status === 'start').map((p) => p.unitId)
  );
  const idleUnits = units.length - activeUnitIds.size;

  // Aggregate processes by type
  const processByType = useMemo(() => {
    const typeMap = new Map<string, number>();
    filteredProcesses
      .filter((p) => p.status === 'start' && p.type)
      .forEach((p) => {
        const count = typeMap.get(p.type!) || 0;
        typeMap.set(p.type!, count + 1);
      });
    return Array.from(typeMap.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .slice(0, 3); // Show top 3 process types
  }, [filteredProcesses]);

  // Calculate process statistics for progress bar (as percentage of total units)
  const processStats = useMemo(() => {
    const typeMap = new Map<string, number>();
    const activeProcesses = filteredProcesses.filter((p) => p.status === 'start' && p.type);

    activeProcesses.forEach((p) => {
      const count = typeMap.get(p.type!) || 0;
      typeMap.set(p.type!, count + 1);
    });

    const totalUnits = units.length;
    const stats = Array.from(typeMap.entries())
      .map(([type, count]) => ({
        type,
        count,
        percentage: totalUnits > 0 ? (count / totalUnits) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    // Add idle units if there are any
    if (idleUnits > 0) {
      stats.push({
        type: 'idle',
        count: idleUnits,
        percentage: totalUnits > 0 ? (idleUnits / totalUnits) * 100 : 0,
      });
    }

    return stats;
  }, [filteredProcesses, units.length, idleUnits]);

  // Determine if we're in "process view mode" or "date view mode" based on viewType prop
  const isProcessViewMode = viewType === 'process';
  const isDateViewMode = viewType === 'date';

  // Helper function to determine time period for date view (defined before useMemo)
  // Use the shared date period calculator
  const getDatePeriod = useCallback((date: Date) => {
    return getDatePeriodHelper(date);
  }, []);

  // Calculate date-based statistics for date view
  const dateStats = useMemo(() => {
    if (!actionItems || !isDateViewMode) {
      return [];
    }

    // Period labels and colors from shared constants
    const periodConfig = {
      overdue: { label: 'Overdue', color: DATE_PERIOD_COLORS.overdue },
      this_week: { label: 'This Week', color: DATE_PERIOD_COLORS.this_week },
      next_week: { label: 'Next Week', color: DATE_PERIOD_COLORS.next_week },
      week_3: { label: 'Week 3', color: DATE_PERIOD_COLORS.week_3 },
      week_4: { label: 'Week 4', color: DATE_PERIOD_COLORS.week_4 },
      week_5_plus: { label: 'Week 5+', color: DATE_PERIOD_COLORS.week_5_plus },
      no_date: { label: 'Unscheduled', color: DATE_PERIOD_COLORS.no_date },
    };

    // Map units to their earliest task due date
    const unitPeriodMap = new Map<string, string>();

    actionItems
      .filter((task) => task.status !== 'done' && task.dueDate && task.unitId)
      .forEach((task) => {
        const unitId = task.unitId!;
        const date = convertPseudoDateToRealDate(task.dueDate!);

        if (date) {
          const existing = unitPeriodMap.get(unitId);
          if (!existing) {
            const period = getDatePeriod(date);
            unitPeriodMap.set(unitId, period);
          }
        }
      });

    // Count units per period
    const periodMap = new Map<string, number>();
    unitPeriodMap.forEach((period) => {
      const count = periodMap.get(period) || 0;
      periodMap.set(period, count + 1);
    });

    // Count unscheduled units
    const scheduledUnitIds = new Set(unitPeriodMap.keys());
    const unscheduledCount = units.filter((u) => !scheduledUnitIds.has(u.id)).length;
    if (unscheduledCount > 0) {
      periodMap.set('no_date', unscheduledCount);
    }

    const totalUnits = units.length;
    const stats = Array.from(periodMap.entries())
      .map(([period, count]) => ({
        period,
        count,
        percentage: totalUnits > 0 ? (count / totalUnits) * 100 : 0,
        color: periodConfig[period as keyof typeof periodConfig]?.color || '#6B7280',
        label: periodConfig[period as keyof typeof periodConfig]?.label || period,
      }))
      .sort((a, b) => {
        // Sort by period order (overdue first, then chronological)
        const order = ['overdue', 'this_week', 'next_week', 'week_3', 'week_4', 'week_5_plus', 'no_date'];
        return order.indexOf(a.period) - order.indexOf(b.period);
      });

    return stats;
  }, [actionItems, units, isDateViewMode, getDatePeriod]);

  // Calculate on-time/overdue statistics for date view
  const taskTimingStats = useMemo(() => {
    if (!actionItems || !isDateViewMode) {
      return { onTime: 0, overdue: 0, totalTasks: 0 };
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    let onTime = 0;
    let overdue = 0;

    actionItems
      .filter((task) => task.status !== 'done' && task.dueDate)
      .forEach((task) => {
        const dueDate = convertPseudoDateToRealDate(task.dueDate!);
        if (dueDate) {
          if (dueDate.getTime() < now.getTime()) {
            overdue++;
          } else {
            onTime++;
          }
        }
      });

    return { onTime, overdue, totalTasks: onTime + overdue };
  }, [actionItems, isDateViewMode]);

  // Filter units based on unit process filter (active/idle)
  const filteredUnits = useMemo(() => {
    if (unitProcessFilter.length === 0) {
      return units;
    }

    const hasActive = unitProcessFilter.includes('active');
    const hasIdle = unitProcessFilter.includes('idle');

    return units.filter((unit) => {
      const isActive = activeUnitIds.has(unit.id);
      if (hasActive && hasIdle) return true; // Show all
      if (hasActive) return isActive;
      if (hasIdle) return !isActive;
      return true;
    });
  }, [units, unitProcessFilter, activeUnitIds]);

  // Recalculate stats based on filtered units
  const filteredStats = useMemo(() => {
    const statsObj: Record<LeaseLifecycle, { units: number; percentage: number }> = {
      [LeaseLifecycle.LEASE]: { units: 0, percentage: 0 },
      [LeaseLifecycle.NOTICE]: { units: 0, percentage: 0 },
      [LeaseLifecycle.INSPECT]: { units: 0, percentage: 0 },
      [LeaseLifecycle.TURNOVER]: { units: 0, percentage: 0 },
      [LeaseLifecycle.DONE]: { units: 0, percentage: 0 },
    };

    filteredUnits.forEach((unit) => {
      const state = Array.isArray(unit.currentState)
        ? unit.currentState[0]
        : unit.currentState || LeaseLifecycle.NOTICE;

      if (statsObj[state]) {
        statsObj[state].units += 1;
      }
    });

    // Calculate percentages
    Object.keys(statsObj).forEach((key) => {
      const status = key as LeaseLifecycle;
      statsObj[status].percentage = filteredUnits.length > 0
        ? statsObj[status].units / filteredUnits.length
        : 0;
    });

    return statsObj;
  }, [filteredUnits]);

  // Create filtered statusFilter for BuildingDisplayModelGeneric based on unit process filter
  const effectiveStatusFilter = (() => {
    if (unitProcessFilter.length === 0) {
      return statusFilter as LeaseLifecycle[];
    }

    // If filtering by active/idle, we need to highlight specific units
    // For now, pass through the original statusFilter
    // In a full implementation, you might want to filter units shown in the 3D model
    return statusFilter as LeaseLifecycle[];
  })();

  // Calculate apartment data and visualizer for process view mode
  const { apartmentData, visualizer } = useMemo(() => {
    if (isDateViewMode && actionItems) {
      // DATE VIEW MODE: Color by earliest task due date
      const unitDateMap = new Map<string, { date: Date; period: string }>();

      // Map units to their earliest task due date (exclude completed tasks)
      actionItems
        .filter((task) => task.status !== 'done' && task.dueDate && task.unitId)
        .forEach((task) => {
          const unitId = task.unitId!;
          const date = convertPseudoDateToRealDate(task.dueDate!);

          if (date) {
            const existing = unitDateMap.get(unitId);
            // Keep the earliest date for each unit
            if (!existing || date.getTime() < existing.date.getTime()) {
              const period = getDatePeriod(date);
              unitDateMap.set(unitId, { date, period });
            }
          }
        });

      // Define color map for time periods from shared constants
      // If filters are active, only show colors for selected periods (others become white)
      const hasActiveFilter = datePeriodFilter.length > 0;
      const dateColorMap = new Map<string, string>([
        ['overdue', hasActiveFilter && !datePeriodFilter.includes('overdue') ? '#ffffff' : DATE_PERIOD_COLORS.overdue],
        ['this_week', hasActiveFilter && !datePeriodFilter.includes('this_week') ? '#ffffff' : DATE_PERIOD_COLORS.this_week],
        ['next_week', hasActiveFilter && !datePeriodFilter.includes('next_week') ? '#ffffff' : DATE_PERIOD_COLORS.next_week],
        ['week_3', hasActiveFilter && !datePeriodFilter.includes('week_3') ? '#ffffff' : DATE_PERIOD_COLORS.week_3],
        ['week_4', hasActiveFilter && !datePeriodFilter.includes('week_4') ? '#ffffff' : DATE_PERIOD_COLORS.week_4],
        ['week_5_plus', hasActiveFilter && !datePeriodFilter.includes('week_5_plus') ? '#ffffff' : DATE_PERIOD_COLORS.week_5_plus],
        ['no_date', hasActiveFilter && !datePeriodFilter.includes('unscheduled') ? '#ffffff' : '#ffffff'],        // Always white for units without scheduled tasks
      ]);

      // Get distinct periods
      const distinctPeriods = new Set<string>();
      unitDateMap.forEach(({ period }) => distinctPeriods.add(period));
      const hasUnscheduled = units.some(u => !unitDateMap.has(u.id));
      if (hasUnscheduled) {
        distinctPeriods.add('no_date');
      }

      // Create visualizer
      const dateVisualizer = new GenericDistinctVisualizer(dateColorMap);

      // Create apartment data
      const data = units.map((unit) => {
        const unitData = unitDateMap.get(unit.id);
        const period = unitData ? unitData.period : 'no_date';
        return {
          apartmentId: unit.id,
          value: period,
          label: period.replace(/_/g, ' '),
        };
      });

      return {
        apartmentData: data,
        visualizer: dateVisualizer,
      };
    } else if (isProcessViewMode && processes) {
      // PROCESS VIEW MODE: Color by process type
      // Create a map of unit IDs to their active process types
      const unitProcessMap = new Map<string, string>();
      filteredProcesses
        .filter((p) => p.status === 'start' && p.type && p.unitId)
        .forEach((p) => {
          // If a unit already has a process, keep the first one (or could use priority logic)
          const processType = p.type!;
          const unitId = p.unitId!;
          if (!unitProcessMap.has(unitId)) {
            unitProcessMap.set(unitId, processType);
          }
        });

      // Get all distinct process types (including 'idle')
      const distinctTypes = new Set<string>();
      unitProcessMap.forEach((type) => distinctTypes.add(type));
      if (idleUnits > 0) {
        distinctTypes.add('idle');
      }

      // Create color map
      const colorMap = new Map<string, string>();
      Array.from(distinctTypes).forEach((type) => {
        if (type === 'idle') {
          colorMap.set(type, '#e5e7eb'); // Light gray for idle
        } else {
          const config = getProcessTypeConfig(type);
          colorMap.set(type, config.color);
        }
      });

      // Create visualizer
      const processVisualizer = new GenericDistinctVisualizer(colorMap);

      // Create apartment data for all units
      const data = units.map((unit) => {
        const processType = unitProcessMap.get(unit.id) || 'idle';
        return {
          apartmentId: unit.id,
          value: processType,
          label: processType,
        };
      });

      return {
        apartmentData: data,
        visualizer: processVisualizer,
      };
    }

    return { apartmentData: undefined, visualizer: undefined };
  }, [isProcessViewMode, isDateViewMode, processes, actionItems, filteredProcesses, units, idleUnits, getProcessTypeConfig, getDatePeriod, datePeriodFilter]);

  return (
    <ErrorBoundary fallback={<p>Error loading project card</p>}>
      <Card
        variant="elevation"
        elevation={0}
        className="flex flex-col bg-zinc-900 border border-white/5 rounded-lg p-16 hover:border-white/20 transition-all duration-200 h-full !outline-none"
        sx={{ width: '100%', minHeight: '380px', outline: 'none' }}
      >
        <Stack gap="16px" className="relative">
          <Stack
            direction="row"
            alignItems="center"
            className="absolute right-8 top-8 z-10"
          >
            {userData?.isDev && isEditMode ? (
              <>
                <IconButton color="primary" onClick={handleEditProject}>
                  <EditOutlinedIcon />
                </IconButton>
                <IconButton color="primary" onClick={handleDeleteProject}>
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </>
            ) : null}
            {hasActiveAction && (
              <ErrorOutlinedIcon className="text-[44px] text-white" />
            )}
          </Stack>

          <CardMedia
            component="div"
            className="center flex w-full rounded-md bg-white bg-opacity-10 object-fill"
            sx={{ height: `${(height || 380) * 0.55}px` }}
          >
            {building && (
              <Stack alignItems="center" width="100%">
                <BuildingDisplayModelGeneric
                  building={building}
                  projectId={projectId}
                  statusFilter={effectiveStatusFilter}
                  apartmentData={apartmentData}
                  visualizer={visualizer}
                />
              </Stack>
            )}
          </CardMedia>
        </Stack>

        <Stack gap="4px" margin="12px 0 0 0">
          <Link to={`/m/projects/${projectId}/${buildingId}/`} className="no-underline group">
            <Typography variant="h6" className="font-semibold text-stone-200 group-hover:text-white transition-colors line-clamp-1" title={name}>
              {name}
            </Typography>
          </Link>

          <Link to={`/m/projects/${projectId}/${buildingId}/`} className="no-underline">
            <Typography variant="body2" className="truncate text-blue-500 hover:text-blue-400 transition-colors cursor-pointer text-sm">
              <Tooltip placement="bottom" className="z-[20]" title={address}>
                <span>{address}</span>
              </Tooltip>
            </Typography>
          </Link>
        </Stack>

        <div className="w-full border-t border-white/5 my-12" />

        <Stack gap="8px" className="flex-1">
          {/* Indicators - show task timing in date view, process info in process view */}
          {isDateViewMode && actionItems ? (
            <Stack gap="6px" className="mt-auto">
              <Stack direction="row" gap="12px">
                <Tooltip title="Tasks that are on schedule">
                  <Stack direction="row" gap="6px" alignItems="center" className="bg-zinc-800/70 px-8 py-4 rounded">
                    <div className="w-[8px] h-[8px] rounded-full bg-green-500"></div>
                    <Typography variant="caption" className="text-stone-300 font-medium">
                      {taskTimingStats.onTime} on time
                    </Typography>
                  </Stack>
                </Tooltip>
                <Tooltip title="Tasks that are past their due date">
                  <Stack direction="row" gap="6px" alignItems="center" className="bg-zinc-800/70 px-8 py-4 rounded">
                    <div className="w-[8px] h-[8px] rounded-full bg-red-500"></div>
                    <Typography variant="caption" className="text-stone-300 font-medium">
                      {taskTimingStats.overdue} overdue
                    </Typography>
                  </Stack>
                </Tooltip>
              </Stack>
            </Stack>
          ) : processes && (
            <Stack gap="8px" className="mt-auto mb-8">
              <Stack direction="row" gap="8px">
                <Tooltip title="Active Processes">
                  <Stack direction="row" gap="6px" alignItems="center" className="bg-zinc-800/70 px-8 py-4 rounded">
                    <div className="w-[8px] h-[8px] rounded-full bg-green-500"></div>
                    <Typography variant="caption" className="text-stone-300 font-medium">
                      {activeProcessCount} active
                    </Typography>
                  </Stack>
                </Tooltip>
                <Tooltip title="Idle Units (no active process)">
                  <Stack direction="row" gap="6px" alignItems="center" className="bg-zinc-800/70 px-8 py-4 rounded">
                    <div className="w-[8px] h-[8px] rounded-full bg-zinc-500"></div>
                    <Typography variant="caption" className="text-stone-300 font-medium">
                      {idleUnits} idle
                    </Typography>
                  </Stack>
                </Tooltip>
              </Stack>

              {/* Process Type Breakdown */}
              {processByType.length > 0 && (
                <Stack direction="row" gap="6px" flexWrap="wrap">
                  {processByType.map(([type, count]) => {
                    const config = getProcessTypeConfig(type);
                    return (
                      <Stack
                        key={type}
                        direction="row"
                        gap="4px"
                        alignItems="center"
                        className="rounded px-8 py-4"
                        sx={{
                          backgroundColor: `${config.color}15`,
                          border: `1px solid ${config.color}30`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: config.color, fontWeight: 600 }}
                        >
                          {count}
                        </Typography>
                        <Typography variant="caption" className="text-stone-400 truncate max-w-[100px]">
                          {config.displayName}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>

        {/* Progress Bar - Show process bars in process view mode, date bars in date view, otherwise lease lifecycle */}
        <Stack direction="row" alignItems="center" gap="4px" className="w-full">
          {isDateViewMode ? (
            // Date view mode - show date period distribution
            dateStats.map((stat) => (
              <DateStatusBar
                key={stat.period}
                period={stat.period}
                count={stat.count}
                percentage={stat.percentage}
                color={stat.color}
                label={stat.label}
              />
            ))
          ) : isProcessViewMode ? (
            // Process view mode - show process type distribution
            processStats.map((stat) => (
              <ProcessStatusBar
                key={stat.type}
                type={stat.type}
                count={stat.count}
                percentage={stat.percentage}
              />
            ))
          ) : (
            // Default view - show lease lifecycle distribution
            Object.values(LeaseLifecycle).map((status) =>
              filteredStats[status].units ? (
                <StatusBar
                  key={status}
                  status={status}
                  units={filteredStats[status].units}
                  percentage={filteredStats[status].percentage}
                />
              ) : undefined
            )
          )}
        </Stack>

        <Modal open={!!isModalOpen} onClose={handleCloseModal}>
          <Box
            component="div"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[10px] border border-stone-200 border-opacity-[0.15] shadow outline-none"
          >
            <NewProjectModalContent
              onClose={handleCloseModal}
              projectId={projectId}
            />
          </Box>
        </Modal>
      </Card>
    </ErrorBoundary>
  );
}
